import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Dialog } from 'Components/ui';
import { DetailSection } from 'Components/common/DetailSection';
import { DEFAULT_TIMEZONE, formatDateOnlyWithTimezone, formatTimeOnlyWithTimezone, formatTimezoneLabel } from 'utils/date_utils';
import { resolveActionLabel } from './action-label.js';
import { resolveResultLabel } from './result-label.js';

// Full-record view for one audit-log row, shared by Tenant/index.jsx and Global/index.jsx.
// Structure follows TransparencyCenter's PolicyDetailModal (Dialog.custom + labeled info grid +
// content papers + a single Close action). Renders the already-loaded list row as-is — the
// payloads arrive PII-masked from the API (write-time for new-shape records, on read for
// legacy ones) and are displayed exactly as masked; nothing here attempts to reverse that.

const InfoSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionLabel = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--vo-slate);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PayloadGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PayloadPaper = styled.pre`
  margin: 0;
  padding: 12px 16px;
  background: var(--vo-mist);
  border: 1px solid var(--vo-cloud);
  border-radius: var(--radius-lg);
  max-height: 320px;
  overflow: auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  color: var(--vo-ink);
  white-space: pre-wrap;
  word-break: break-word;
`;

const MaskedNote = styled.p`
  margin: 12px 0 0 0;
  font-size: 12px;
  color: var(--vo-slate);
`;

// Pretty-prints a JSON object/array payload; any other string (plain legacy text, a bare
// count, a legacy "id: … e {…}" template) is shown verbatim; empty/absent → null ('—').
export const formatPayload = (value) => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value !== 'string') return JSON.stringify(value, null, 2);
  const trimmed = value.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return value;
    }
  }
  return value;
};

const PayloadField = ({ label, value }) => {
  const formatted = formatPayload(value);
  return (
    <DetailSection.Field label={label}>
      {formatted === null ? '—' : <PayloadPaper>{formatted}</PayloadPaper>}
    </DetailSection.Field>
  );
};

PayloadField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
};

export const AuditLogDetailDialog = ({ log, t, companyTimezone, companyLocale, companyName, onClose }) => {
  if (!log) return null;
  const actorType = log.actorType
    ? t(`audit:page.detail.actorTypes.${log.actorType}`, { defaultValue: log.actorType })
    : t('audit:page.detail.notRecorded');

  return (
    <Dialog.custom
      open
      onClose={onClose}
      size="lg"
      title={t('audit:page.detail.title')}
      actions={<Button onClick={onClose}>{t('common:buttons.close')}</Button>}
    >
      <InfoSection>
        <SectionLabel>{t('audit:page.detail.general')}</SectionLabel>
        <InfoGrid>
          <DetailSection.Field label={t('audit:page.detail.fields.date')} value={formatDateOnlyWithTimezone(log.displayTimestamp, companyTimezone, companyLocale) || null} />
          <DetailSection.Field label={t('audit:page.detail.fields.time')} value={formatTimeOnlyWithTimezone(log.displayTimestamp, companyTimezone, companyLocale) || null} />
          <DetailSection.Field
            label={t('audit:page.detail.fields.timezone')}
            value={`${companyTimezone || DEFAULT_TIMEZONE} (${formatTimezoneLabel(companyTimezone, log.displayTimestamp || undefined, companyLocale)})`}
          />
          <DetailSection.Field label={t('audit:page.detail.fields.createdAt')} value={log.createdAt ? new Date(log.createdAt).toISOString() : null} />
          <DetailSection.Field label={t('audit:page.detail.fields.service')} value={log.service || null} />
          <DetailSection.Field label={t('audit:page.detail.fields.user')} value={log.user || null} />
          <DetailSection.Field label={t('audit:page.detail.fields.userId')} value={log.userId || null} />
          <DetailSection.Field label={t('audit:page.detail.fields.actorType')} value={actorType} />
          <DetailSection.Field label={t('audit:page.detail.fields.action')} value={resolveActionLabel(t, log) || null} />
          <DetailSection.Field label={t('audit:page.detail.fields.result')} value={resolveResultLabel(t, log) || null} />
          {log.resultDetail && (
            <DetailSection.Field label={t('audit:page.detail.fields.resultDetail')} value={log.resultDetail} />
          )}
          {companyName !== undefined && (
            <DetailSection.Field label={t('audit:page.detail.fields.company')} value={companyName || null} />
          )}
        </InfoGrid>
      </InfoSection>
      <InfoSection>
        <SectionLabel>{t('audit:page.detail.payload')}</SectionLabel>
        <PayloadGrid>
          <PayloadField label={t('audit:page.detail.fields.dataBefore')} value={log.dataBeforeAction} />
          <PayloadField label={t('audit:page.detail.fields.dataAfter')} value={log.dataAfterAction} />
        </PayloadGrid>
        <MaskedNote>{t('audit:page.detail.maskedNote')}</MaskedNote>
      </InfoSection>
    </Dialog.custom>
  );
};

AuditLogDetailDialog.propTypes = {
  log: PropTypes.object,
  t: PropTypes.func.isRequired,
  companyTimezone: PropTypes.string,
  companyLocale: PropTypes.string,
  // Global page only — Tenant has no company column/filter by design, so it omits this.
  companyName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
